import { useEffect, useRef } from 'react';
import { useGetEducationProgressionPrompt } from 'hooks/api/backend/user/education-progression';
import { SessionStorageKey, useSessionStorage } from 'hooks/useSessionStorage';
import { useModalService } from 'hooks/useModalService';
import { ModalVariant } from 'types/Modal';
import { useUser } from 'utils/auth';
import { useEducationProgressionModalHandlers } from './useProgressionHandlers';

type UseEducationProgressionPromptModalOptions = {
  enabled?: boolean;
};

const markPromptHandled = (
  handledPromptIdsStorage: ReturnType<typeof useSessionStorage<string[]>>,
  promptId: string
): void => {
  const handledIds = handledPromptIdsStorage.getValue() ?? [];

  if (handledIds.includes(promptId)) {
    return;
  }

  handledPromptIdsStorage.setValue([...handledIds, promptId]);
};

const isAutoOpenBlocked = ({
  activeModal,
  shouldShowOnboardingModal,
}: {
  activeModal: ModalVariant | null;
  shouldShowOnboardingModal: boolean;
}): boolean => {
  if (shouldShowOnboardingModal) {
    return true;
  }

  if (activeModal !== null) {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  const hasCasemePending = !!sessionStorage.getItem(SessionStorageKey.CASEME_NEWSLETTER_AWAITING);
  const isOnboardingModalDeferred =
    sessionStorage.getItem(SessionStorageKey.DEFER_ONBOARDING_MODAL) === 'true';

  return hasCasemePending || isOnboardingModalDeferred;
};

export const useEducationProgressionPromptModal = (
  options: UseEducationProgressionPromptModalOptions = {}
): void => {
  const { enabled: enabledOption = true } = options;
  const { data: user } = useUser();
  const { show, activeModal } = useModalService();
  const handledPromptIdsStorage = useSessionStorage<string[]>(
    SessionStorageKey.EDUCATION_PROGRESSION_HANDLED_PROMPT_IDS,
    []
  );
  const openedPromptIdRef = useRef<string | null>(null);

  const isQueryEnabled = enabledOption && !!user && !user.isFirstTimeUse;

  const { data: prompt, isSuccess } = useGetEducationProgressionPrompt({
    enabled: isQueryEnabled,
  });

  const handlers = useEducationProgressionModalHandlers(prompt ?? null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!isQueryEnabled || !isSuccess || !prompt) {
      return;
    }

    const handledIds = handledPromptIdsStorage.getValue() ?? [];
    if (handledIds.includes(prompt.id)) {
      return;
    }

    if (openedPromptIdRef.current === prompt.id) {
      return;
    }

    if (
      isAutoOpenBlocked({
        activeModal,
        shouldShowOnboardingModal: !!user?.shouldShowOnboardingModal,
      })
    ) {
      return;
    }

    openedPromptIdRef.current = prompt.id;
    markPromptHandled(handledPromptIdsStorage, prompt.id);

    const { onAdjust, onConfirm, onDismiss, isAdjusting, isConfirming } = handlersRef.current;

    show(ModalVariant.EDUCATION_PROGRESSION, {
      educationProgressionPrompt: prompt,
      onAdjust: async (data) => {
        await onAdjust(data);
        markPromptHandled(handledPromptIdsStorage, prompt.id);
      },
      onConfirm: async () => {
        await onConfirm();
        markPromptHandled(handledPromptIdsStorage, prompt.id);
      },
      onDismiss: async () => {
        await onDismiss();
        markPromptHandled(handledPromptIdsStorage, prompt.id);
      },
      isSubmitting: isAdjusting || isConfirming,
    });
  }, [
    activeModal,
    handledPromptIdsStorage,
    isQueryEnabled,
    isSuccess,
    prompt,
    show,
    user?.shouldShowOnboardingModal,
  ]);
};
